import React, { useCallback, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Trash2, Undo2, Plus } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['places'];
const TROIA_CENTER = { lat: 38.455, lng: -8.87 };
const ZONE_COLORS = ['#FF6B35', '#2EC4B6', '#7C4DFF', '#F9A825', '#E53935', '#00897B'];

export const DeliveryZoneEditor = ({ zones, onChange }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const list = zones || [];
  const sel = list[selectedIdx] || null;

  const updateZone = (idx, patch) => {
    const next = list.map((z, i) => (i === idx ? { ...z, ...patch } : z));
    onChange(next);
  };

  const addZone = () => {
    const n = list.length + 1;
    const next = [...list, {
      zone_id: `zone_${Date.now()}`,
      name: `Zona ${n}`,
      color: ZONE_COLORS[list.length % ZONE_COLORS.length],
      enabled: true,
      points: [],
      pricing_mode: 'km',
      fixed_fee: 0,
    }];
    onChange(next);
    setSelectedIdx(next.length - 1);
  };

  const removeZone = (idx) => {
    if (!window.confirm(`Eliminar "${list[idx]?.name}"?`)) return;
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
    setSelectedIdx(Math.max(0, Math.min(selectedIdx, next.length - 1)));
  };

  const onMapClick = useCallback((e) => {
    if (!sel) return;
    const lat = +e.latLng.lat().toFixed(6);
    const lng = +e.latLng.lng().toFixed(6);
    updateZone(selectedIdx, { points: [...(sel.points || []), [lat, lng]] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, selectedIdx, list]);

  const onMarkerDrag = (i, e) => {
    const pts = [...(sel.points || [])];
    pts[i] = [+e.latLng.lat().toFixed(6), +e.latLng.lng().toFixed(6)];
    updateZone(selectedIdx, { points: pts });
  };

  if (loadError) return <p className="text-sm text-red-600">Erro ao carregar o mapa</p>;
  if (!isLoaded) return <p className="text-sm text-[#9FA2B4]">A carregar mapa...</p>;

  return (
    <div className="space-y-4" data-testid="delivery-zone-editor">
      {/* Zone list */}
      <div className="flex flex-wrap gap-2 items-center">
        {list.map((z, i) => (
          <button
            key={z.zone_id || i}
            onClick={() => setSelectedIdx(i)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
              i === selectedIdx ? 'border-[#004E64] bg-[#004E64] text-white' : 'border-[#004E64]/20 hover:border-[#004E64]/50'
            }`}
            data-testid={`zone-chip-${i}`}
          >
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: z.color }} />
            {z.name} {!z.enabled && <span className="opacity-60">(off)</span>}
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={addZone} className="rounded-full" data-testid="add-zone-btn">
          <Plus size={14} className="mr-1" /> Adicionar zona
        </Button>
      </div>

      {sel && (
        <div className="p-4 rounded-2xl border border-[#004E64]/10 bg-[#FDFBF7] space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Nome</Label>
              <Input
                value={sel.name}
                onChange={(e) => updateZone(selectedIdx, { name: e.target.value })}
                className="w-44" data-testid="zone-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Cor</Label>
              <div className="flex gap-1.5 pt-1">
                {ZONE_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => updateZone(selectedIdx, { color: c })}
                    className={`w-6 h-6 rounded-full border-2 ${sel.color === c ? 'border-[#004E64]' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`zone-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                checked={!!sel.enabled}
                onCheckedChange={(v) => updateZone(selectedIdx, { enabled: v })}
                data-testid="zone-enabled-toggle"
              />
              <Label className="text-sm">Ativa</Label>
            </div>
            <Button variant="ghost" size="sm" onClick={() => removeZone(selectedIdx)} className="text-red-500 pb-1" data-testid="zone-delete-btn">
              <Trash2 size={14} className="mr-1" /> Eliminar zona
            </Button>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Regra de preço da entrega</Label>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => updateZone(selectedIdx, { pricing_mode: 'km' })}
                  className={`px-3 py-1.5 rounded-full text-sm border ${sel.pricing_mode !== 'fixed' ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-[#004E64]/20'}`}
                  data-testid="zone-mode-km"
                >
                  Por distância (km)
                </button>
                <button
                  onClick={() => updateZone(selectedIdx, { pricing_mode: 'fixed' })}
                  className={`px-3 py-1.5 rounded-full text-sm border ${sel.pricing_mode === 'fixed' ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-[#004E64]/20'}`}
                  data-testid="zone-mode-fixed"
                >
                  Preço fixo
                </button>
              </div>
            </div>
            {sel.pricing_mode === 'fixed' && (
              <div>
                <Label className="text-xs">Taxa de entrega fixa (€)</Label>
                <Input
                  type="number" step="0.5"
                  value={sel.fixed_fee ?? 0}
                  onChange={(e) => updateZone(selectedIdx, { fixed_fee: parseFloat(e.target.value) || 0 })}
                  className="w-28" data-testid="zone-fixed-fee"
                />
              </div>
            )}
          </div>
          <p className="text-xs text-[#9FA2B4]">
            Clica no mapa para adicionar pontos a <b>{sel.name}</b>. Arrasta para ajustar. Mínimo 3 pontos. ({(sel.points || []).length} pontos)
          </p>
        </div>
      )}

      <div className="rounded-xl overflow-hidden border">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '420px' }}
          center={sel?.points?.length ? { lat: sel.points[0][0], lng: sel.points[0][1] } : TROIA_CENTER}
          zoom={12}
          onClick={onMapClick}
          options={{ streetViewControl: false, mapTypeControl: true, fullscreenControl: true }}
        >
          {list.map((z, i) => (
            (z.points || []).length >= 3 && (
              <Polygon
                key={z.zone_id || i}
                paths={z.points.map(([lat, lng]) => ({ lat, lng }))}
                options={{
                  fillColor: z.color, fillOpacity: i === selectedIdx ? 0.22 : 0.08,
                  strokeColor: z.color, strokeWeight: i === selectedIdx ? 3 : 1.5,
                  clickable: false,
                }}
              />
            )
          ))}
          {(sel?.points || []).map(([lat, lng], i) => (
            <Marker
              key={i}
              position={{ lat, lng }}
              draggable
              onDragEnd={(e) => onMarkerDrag(i, e)}
              label={{ text: `${i + 1}`, color: '#fff', fontSize: '11px' }}
            />
          ))}
        </GoogleMap>
      </div>
      {sel && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => updateZone(selectedIdx, { points: (sel.points || []).slice(0, -1) })} disabled={!(sel.points || []).length} className="rounded-full" data-testid="zone-undo-point">
            <Undo2 size={14} className="mr-1" /> Remover último ponto
          </Button>
          <Button variant="outline" size="sm" onClick={() => updateZone(selectedIdx, { points: [] })} disabled={!(sel.points || []).length} className="rounded-full text-red-500" data-testid="zone-clear">
            <Trash2 size={14} className="mr-1" /> Limpar pontos
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeliveryZoneEditor;
